import {Directive, ElementRef, EventEmitter, OnDestroy} from '@angular/core';
import {AbstractControl, FormGroup, NgForm} from '@angular/forms';
import {ErrorCollection} from '../../util/ErrorCollection';
import {IValidationRuleError} from '../../rules/rules';
import {ValidationContext} from '../../data/validation-context';

export class ValidationSubmitEvent<T = any> {
    public static readonly INVALID = 'invalid';
    public static readonly VALID = 'valid';

    constructor(
        public readonly type: string,
        public readonly target: EventTarget,
        public readonly formData?: T,
        public readonly context?: ValidationContext,
        public readonly localContext?: ValidationContext,
        public readonly errorCollection?: ErrorCollection,
        public readonly errors?: any,
    ) {
    }
}

export type ValidationCheckFieldErrors = Record<string, IValidationRuleError>;
export type ValidationCheckGroupErrors = {
    [key: string]: ValidationCheckFieldErrors | ValidationCheckGroupErrors
};

@Directive({})
export abstract class ValidationCheckBaseDirective implements OnDestroy {

    protected static markDirtyRecursively($control: AbstractControl | FormGroup): void {
        $control.markAsDirty({onlySelf: true});
        if ($control instanceof FormGroup) {
            Object.keys($control.controls).forEach(($controlName: string): void => {
                ValidationCheckBaseDirective.markDirtyRecursively($control.controls[$controlName]);
            });
        }
    }

    protected static extractErrorFromControl($control: AbstractControl): ValidationCheckFieldErrors | ValidationCheckGroupErrors {
        if ($control instanceof FormGroup) {
            return Object.keys($control.controls)
                .reduce(
                    ($acc: ValidationCheckGroupErrors, $controlName: string): ValidationCheckGroupErrors => {
                        const control = $control.controls[$controlName];
                        const errors = ValidationCheckBaseDirective.extractErrorFromControl(control);
                        if (errors) {
                            return {
                                ...$acc,
                                [$controlName]: errors,
                            };
                        }
                        return $acc;
                    },
                    null
                );
        }
        return $control.errors;
    }

    protected readonly onValid$ = new EventEmitter<ValidationSubmitEvent>();
    protected readonly onInvalid$ = new EventEmitter<ValidationSubmitEvent>();
    protected readonly onValidated$ = new EventEmitter<ValidationSubmitEvent>();

    protected constructor(
        protected readonly element: ElementRef,
        protected readonly errorCollection: ErrorCollection,
        protected readonly globalContext: ValidationContext,
        protected readonly localContext: ValidationContext,
        protected readonly ngForm: NgForm,
    ) {
        if (this.ngForm) {
            this.handleClick = this.handleClick.bind(this);
            this.element.nativeElement.addEventListener('click', this.handleClick);
        }
    }

    protected abstract performSubmit(): void;

    protected handleClick($event: MouseEvent): void {
        if (this.ngForm.enabled) {
            // Trigger form validation.
            this.performSubmit();

            // Just in case this will be required someday, saving this here.
            // this.ngForm.onReset();

            // Clean previous errors if they existed.
            this.errorCollection.clear();

            let event: ValidationSubmitEvent;
            if (this.ngForm.valid) {
                event = new ValidationSubmitEvent(
                    ValidationSubmitEvent.VALID,
                    this.element.nativeElement,
                    this.ngForm.value,
                    this.globalContext,
                    this.localContext,
                    this.errorCollection
                );
                this.onValid$.emit(event);
            } else {
                event = new ValidationSubmitEvent(
                    ValidationSubmitEvent.INVALID,
                    this.element.nativeElement,
                    this.ngForm.value,
                    this.globalContext,
                    this.localContext,
                    this.errorCollection,
                    ValidationCheckBaseDirective.extractErrorFromControl(this.ngForm.form),
                );
                this.onInvalid$.emit(event);
            }
            // Submit any event if this is required.
            this.onValidated$.emit(event);

            // Not sure if this is required;
            $event.preventDefault();
            $event.stopPropagation();
        }
    }

    public ngOnDestroy(): void {
        this.element.nativeElement.removeEventListener('click', this.handleClick);

        this.onInvalid$.complete();
        this.onValid$.complete();
        this.onValidated$.complete();
    }
}
